import React, { useState, useRef } from 'react';
import { 
  FiUpload, FiDownload, FiCheck, FiX, FiAlertCircle, 
  FiFileText, FiSettings, FiPlay, FiPause, FiRefreshCw
} from 'react-icons/fi';

interface ImportField {
  csvColumn: string;
  dbField: string;
  required: boolean;
  type: 'text' | 'number' | 'date' | 'boolean' | 'email';
  example?: string;
}

interface ImportError {
  row: number;
  field: string;
  value: any;
  error: string;
}

interface ImportResult {
  total: number;
  successful: number;
  failed: number;
  errors: ImportError[];
  warnings: ImportError[];
}

interface MassImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entity: 'customers' | 'products' | 'leads' | 'employees';
  onImportComplete: (result: ImportResult) => void;
}

export const MassImportDialog: React.FC<MassImportDialogProps> = ({
  isOpen,
  onClose,
  entity,
  onImportComplete
}) => {
  const [step, setStep] = useState<'upload' | 'mapping' | 'validation' | 'import' | 'results'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [validationResult, setValidationResult] = useState<ImportResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Entity-specific field definitions
  const entityFields: Record<string, ImportField[]> = {
    customers: [
      { csvColumn: '', dbField: 'name', required: true, type: 'text', example: 'Acme Corp' },
      { csvColumn: '', dbField: 'email', required: false, type: 'email', example: 'contact@acme.com' },
      { csvColumn: '', dbField: 'phone', required: false, type: 'text', example: '+1-555-0123' },
      { csvColumn: '', dbField: 'companyName', required: false, type: 'text', example: 'Acme Corporation' },
      { csvColumn: '', dbField: 'industry', required: false, type: 'text', example: 'Technology' },
      { csvColumn: '', dbField: 'contactPerson', required: false, type: 'text', example: 'John Doe' },
      { csvColumn: '', dbField: 'paymentTerms', required: false, type: 'text', example: '30 days' },
      { csvColumn: '', dbField: 'creditLimit', required: false, type: 'number', example: '50000' },
    ],
    products: [
      { csvColumn: '', dbField: 'itemCode', required: true, type: 'text', example: 'PROD001' },
      { csvColumn: '', dbField: 'itemName', required: true, type: 'text', example: 'Sample Product' },
      { csvColumn: '', dbField: 'description', required: false, type: 'text', example: 'Product description' },
      { csvColumn: '', dbField: 'unitOfMeasure', required: false, type: 'text', example: 'Nos' },
      { csvColumn: '', dbField: 'standardSellingRate', required: true, type: 'number', example: '100.00' },
      { csvColumn: '', dbField: 'standardBuyingRate', required: false, type: 'number', example: '80.00' },
      { csvColumn: '', dbField: 'minStock', required: false, type: 'number', example: '10' },
      { csvColumn: '', dbField: 'maxStock', required: false, type: 'number', example: '100' },
    ],
    leads: [
      { csvColumn: '', dbField: 'firstName', required: true, type: 'text', example: 'John' },
      { csvColumn: '', dbField: 'lastName', required: true, type: 'text', example: 'Smith' },
      { csvColumn: '', dbField: 'email', required: true, type: 'email', example: 'john@example.com' },
      { csvColumn: '', dbField: 'phone', required: false, type: 'text', example: '+1-555-0123' },
      { csvColumn: '', dbField: 'companyName', required: false, type: 'text', example: 'Example Corp' },
      { csvColumn: '', dbField: 'jobTitle', required: false, type: 'text', example: 'CEO' },
      { csvColumn: '', dbField: 'source', required: false, type: 'text', example: 'website' },
      { csvColumn: '', dbField: 'rating', required: false, type: 'text', example: 'hot' },
    ],
    employees: [
      { csvColumn: '', dbField: 'employeeId', required: true, type: 'text', example: 'EMP001' },
      { csvColumn: '', dbField: 'firstName', required: true, type: 'text', example: 'Jane' },
      { csvColumn: '', dbField: 'lastName', required: true, type: 'text', example: 'Doe' },
      { csvColumn: '', dbField: 'email', required: true, type: 'email', example: 'jane@company.com' },
      { csvColumn: '', dbField: 'department', required: true, type: 'text', example: 'IT' },
      { csvColumn: '', dbField: 'position', required: true, type: 'text', example: 'Software Engineer' },
      { csvColumn: '', dbField: 'hireDate', required: true, type: 'date', example: '2024-01-15' },
      { csvColumn: '', dbField: 'salary', required: true, type: 'number', example: '75000' },
    ]
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const csv = e.target?.result as string;
      const lines = csv.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const data = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      setCsvHeaders(headers);
      setCsvData(data);
      setStep('mapping');
    };

    reader.readAsText(uploadedFile);
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`/api/v1/import/templates/${entity}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${entity}_import_template.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  const handleMappingChange = (dbField: string, csvColumn: string) => {
    setFieldMapping(prev => ({
      ...prev,
      [csvColumn]: dbField
    }));
  };

  const validateData = async () => {
    setIsLoading(true);
    setStep('validation');
    
    try {
      // Simulate validation API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockResult: ImportResult = {
        total: csvData.length,
        successful: Math.floor(csvData.length * 0.9),
        failed: Math.floor(csvData.length * 0.1),
        errors: [
          { row: 3, field: 'email', value: 'invalid-email', error: 'Invalid email format' },
          { row: 7, field: 'phone', value: '123', error: 'Phone number too short' }
        ],
        warnings: [
          { row: 5, field: 'companyName', value: '', error: 'Company name is empty but not required' }
        ]
      };
      
      setValidationResult(mockResult);
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const performImport = async () => {
    setIsLoading(true);
    setStep('import');
    setProgress(0);
    
    try {
      setProgress(10);
      
      // Map CSV data to API format
      const mappedData = csvData.map(row => {
        const mappedRow: any = {};
        
        // Apply field mapping
        Object.entries(fieldMapping).forEach(([csvColumn, dbField]) => {
          if (row[csvColumn] !== undefined) {
            mappedRow[dbField] = row[csvColumn];
          }
        });
        
        return mappedRow;
      });
      
      setProgress(30);
      
      // Make API call based on entity type
      let apiUrl = '';
      let requestBody: any = {};
      
      switch (entity) {
        case 'products':
          apiUrl = '/api/v1/products/bulk';
          requestBody = { products: mappedData };
          break;
        case 'customers':
          apiUrl = '/api/v1/customers/bulk';
          requestBody = { customers: mappedData };
          break;
        case 'suppliers':
          apiUrl = '/api/v1/suppliers/bulk';
          requestBody = { suppliers: mappedData };
          break;
        default:
          throw new Error(`Unsupported entity type: ${entity}`);
      }
      
      setProgress(50);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      setProgress(80);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setProgress(100);
      
      const importResult: ImportResult = {
        total: result.results?.total || csvData.length,
        successful: result.results?.successful || 0,
        failed: result.results?.failed || 0,
        errors: result.results?.errors?.map((err: any) => ({
          row: err.row,
          field: err.field || 'unknown',
          value: err.value || '',
          error: err.error
        })) || [],
        warnings: []
      };
      
      setImportResult(importResult);
      setStep('results');
      onImportComplete(importResult);
    } catch (error) {
      console.error('Import error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetDialog = () => {
    setStep('upload');
    setFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setFieldMapping({});
    setValidationResult(null);
    setImportResult(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            Mass Import - {entity.charAt(0).toUpperCase() + entity.slice(1)}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b">
          <div className="flex items-center space-x-4">
            {[
              { key: 'upload', label: 'Upload', icon: FiUpload },
              { key: 'mapping', label: 'Field Mapping', icon: FiSettings },
              { key: 'validation', label: 'Validation', icon: FiCheck },
              { key: 'import', label: 'Import', icon: FiPlay },
              { key: 'results', label: 'Results', icon: FiFileText }
            ].map((stepInfo, index) => {
              const isActive = step === stepInfo.key;
              const isCompleted = ['upload', 'mapping', 'validation', 'import'].indexOf(step) > 
                                  ['upload', 'mapping', 'validation', 'import'].indexOf(stepInfo.key);
              
              return (
                <div key={stepInfo.key} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    isActive ? 'bg-blue-500 text-white' :
                    isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    <stepInfo.icon size={16} />
                  </div>
                  <span className={`ml-2 text-sm ${isActive ? 'font-medium' : 'text-gray-600'}`}>
                    {stepInfo.label}
                  </span>
                  {index < 4 && <div className="w-8 h-px bg-gray-300 ml-4" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'upload' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                  <FiUpload size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-medium mb-2">Upload CSV or Excel File</p>
                  <p className="text-gray-600 mb-4">Select a file to import {entity} data</p>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
                  >
                    Choose File
                  </button>
                  
                  {file && (
                    <div className="mt-4 text-sm text-gray-600">
                      Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-center">
                <button
                  onClick={downloadTemplate}
                  className="flex items-center space-x-2 text-blue-500 hover:text-blue-600"
                >
                  <FiDownload size={16} />
                  <span>Download Template</span>
                </button>
              </div>
            </div>
          )}

          {step === 'mapping' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-medium">Map CSV Columns to Database Fields</h3>
                <p className="text-gray-600">Match your CSV columns to the correct database fields</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-3">Your CSV Columns</h4>
                  <div className="space-y-2">
                    {csvHeaders.map((header, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded border">
                        {header}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-3">Database Fields</h4>
                  <div className="space-y-2">
                    {entityFields[entity]?.map((field, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <select
                          value={Object.keys(fieldMapping).find(k => fieldMapping[k] === field.dbField) || ''}
                          onChange={(e) => handleMappingChange(field.dbField, e.target.value)}
                          className="flex-1 border rounded px-3 py-2"
                        >
                          <option value="">Select column...</option>
                          {csvHeaders.map(header => (
                            <option key={header} value={header}>{header}</option>
                          ))}
                        </select>
                        <div className="w-32">
                          <span className={`text-sm ${field.required ? 'text-red-600' : 'text-gray-600'}`}>
                            {field.dbField}
                            {field.required && ' *'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep('upload')}
                  className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={validateData}
                  disabled={Object.keys(fieldMapping).length === 0}
                  className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300"
                >
                  Validate Data
                </button>
              </div>
            </div>
          )}

          {step === 'validation' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium">Data Validation</h3>
                <p className="text-gray-600">Checking your data for errors and warnings</p>
              </div>

              {isLoading ? (
                <div className="text-center py-12">
                  <FiRefreshCw size={48} className="mx-auto text-blue-500 animate-spin mb-4" />
                  <p>Validating data...</p>
                </div>
              ) : validationResult ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-600">{validationResult.total}</div>
                      <div className="text-sm text-blue-600">Total Records</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">{validationResult.successful}</div>
                      <div className="text-sm text-green-600">Valid Records</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-red-600">{validationResult.failed}</div>
                      <div className="text-sm text-red-600">Invalid Records</div>
                    </div>
                  </div>

                  {validationResult.errors.length > 0 && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="font-medium text-red-800 mb-2">Errors Found:</h4>
                      <div className="space-y-1">
                        {validationResult.errors.map((error, index) => (
                          <div key={index} className="text-sm text-red-700">
                            Row {error.row}, {error.field}: {error.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {validationResult.warnings.length > 0 && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-800 mb-2">Warnings:</h4>
                      <div className="space-y-1">
                        {validationResult.warnings.map((warning, index) => (
                          <div key={index} className="text-sm text-yellow-700">
                            Row {warning.row}, {warning.field}: {warning.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <button
                      onClick={() => setStep('mapping')}
                      className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50"
                    >
                      Back to Mapping
                    </button>
                    <button
                      onClick={performImport}
                      disabled={validationResult.failed === validationResult.total}
                      className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300"
                    >
                      Proceed with Import
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {step === 'import' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium">Importing Data</h3>
                <p className="text-gray-600">Please wait while we import your data</p>
              </div>

              <div className="py-12">
                <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                  <div 
                    className="bg-blue-500 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="text-center text-sm text-gray-600">
                  {progress}% Complete
                </div>
              </div>
            </div>
          )}

          {step === 'results' && importResult && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium">Import Complete</h3>
                <p className="text-gray-600">Your data has been processed</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{importResult.total}</div>
                  <div className="text-sm text-blue-600">Total Records</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{importResult.successful}</div>
                  <div className="text-sm text-green-600">Successfully Imported</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{importResult.failed}</div>
                  <div className="text-sm text-red-600">Failed to Import</div>
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2">Import Errors:</h4>
                  <div className="space-y-1">
                    {importResult.errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-700">
                        Row {error.row}, {error.field}: {error.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={resetDialog}
                  className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50"
                >
                  Import More Data
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 